import React, { useState, useEffect, useRef } from "react";
import { fetchUserAttributes, signOut } from "aws-amplify/auth";
import { useLocation, useNavigate } from 'react-router-dom';
import "../../assets/css/Navbar.css";
import { useDealData, sortDealPeriods } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";
import { useTabs } from 'contexts/TabContext';
import TimePeriodSelector from "./TimePeriodSelector";
import FacilitySelector from "./DealFacilitySelector";
import PortfolioNameSelector from "./PortfolioSelector";
import PortfolioSelector from "./PortfolioFacilitySelector";
import ViewModeToggle from "components/navigation/ViewModeToggle";
import logo from "assets/img/logo/logo.png";
import miniLogo from "assets/img/logo/logo-icon.jpeg";
import DealSelector from "./DealSelector";
import { getCurrentUser } from "aws-amplify/auth";
import { Link } from "react-router-dom";
import { IoArchiveOutline, IoArchiveSharp } from "react-icons/io5";
import { MdDashboard, MdAssessment, MdLocationCity, MdClose, MdAccountCircle } from "react-icons/md";
import { TbChartLine, TbReportMoney, TbZoomMoney, TbTableOptions } from "react-icons/tb";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import TooltipHorizon from "components/tooltip";


const Navbar = ({ ...rest }) => {
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState(null);
  const [actualUsername, setActualUsername] = useState(null); // For group operations
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const dropdownRef = useRef(null);

  const {
    dealNames,
    currentDealName,
    currentDeal,
    currentFacility,
    selectedPeriod: dealSelectedPeriod,
    selectDeal,
    selectFacility,
    setSelectedPeriod: setDealSelectedPeriod,
    currentGroup,
    selectGroup,
    setCurrentGroup,
    facilityGroups,
    consolidatedData,
    isDealLoading,
    isLoading,
    viewingArchived,
    toggleArchiveView,
    hasArchivedDealsData,
    hasArchiveAccess,
  } = useDealData();

  const {
    portfolioNames,
    portfolioFacilityNames,
    currentPortfolioName,
    currentPortfolioFacilityName,
    currentPortfolioFacility,
    selectedPeriod: portfolioSelectedPeriod,
    selectPortfolio,
    selectPortfolioFacility,
    selectPortfolioGroup,
    setSelectedPeriod: setPortfolioSelectedPeriod,
    isPortfolioLoading,
    periodsList: portfolioPeriodsList,
    currentGroup: currentPortfolioGroup,
    setCurrentGroup: setCurrentPortfolioGroup,
  } = usePortfolioData();

  const { viewMode, changeViewMode, isTransitioning } = useViewMode();

  // Tab functionality
  const location = useLocation();
  const navigate = useNavigate();
  const { dynamicTabs, activeTab, switchToTab, closeTab } = useTabs();

  // Get facility names based on current view mode
  const facilityNames = viewMode === 'portfolio'
    ? portfolioFacilityNames
    : currentDeal
      ? Object.keys(currentDeal.facilities).filter(
        name => name.toLowerCase() !== 'consolidated'
      )
      : [];

  // Get periods from current facility based on view mode
  const periodsList = viewMode === 'portfolio'
    ? portfolioPeriodsList
    : currentFacility
      ? sortDealPeriods(Object.keys(currentFacility.periods || {}))
      : [];



  // Get selected period based on view mode
  const selectedPeriod = viewMode === 'portfolio' ? portfolioSelectedPeriod : dealSelectedPeriod;

  // Get set selected period function based on view mode
  const setSelectedPeriod = viewMode === 'portfolio' ? setPortfolioSelectedPeriod : setDealSelectedPeriod;


  // Screen size effect
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 950);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // User fetch effect - removed permission check as it's now in DealContext
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        setUser(user);

        const attributes = await fetchUserAttributes();
        const name = attributes.given_name || user.username;
        setUsername(name); // For display purposes
        setActualUsername(user.username); // For group operations
        setIsLoadingUser(false);
      } catch (error) {
        console.error("Error fetching user:", error);
        setIsLoadingUser(false);
      }
    };

    fetchUser();
  }, []);

  // Dropdown handlers
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      // Error handling
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const closeDropdown = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", closeDropdown);
    return () => {
      document.removeEventListener("mousedown", closeDropdown);
    };
  }, []);

  useEffect(() => {
    // Function to handle URL changes
    const handleUrlChange = () => {
      const path = window.location.pathname;
      const match = path.match(/\/deals\/([^/]+)/);

      if (match && match[1] && consolidatedData && consolidatedData[match[1]]) {
        const dealNameFromUrl = match[1];

        if (dealNameFromUrl !== currentDealName) {
          selectDeal(dealNameFromUrl);
        }
      }
    };

    // Set up listeners for browser navigation
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [currentDealName, selectDeal, consolidatedData]);

  // Handle deal/group selection
  const handleDealSelection = async (event) => {
    // Avoid duplicate selections
    if (isDealLoading) {
      return;
    }

    // Store current period to check availability after deal change
    const previousPeriod = selectedPeriod;

    // Store current path to check if we're on a finance chart tab
    const currentPath = window.location.pathname.replace(/\/+$/, "");
    const isFinanceChartView = currentPath.includes('/finance-charts/');
    const chartType = isFinanceChartView ? currentPath.split('/finance-charts/')[1]?.split('/')[0] : null;

    if (event.isGroup) {
      // Force a direct selection of the group by its ID
      const groupToSelect = facilityGroups.find(g => g.id === event.groupId);

      if (groupToSelect) {
        setCurrentGroup(groupToSelect);
        // Pass the previous period to maintain it if possible
        await selectGroup(event.groupId, previousPeriod);
      }
    } else {
      const dealName = event.target?.value;

      // Skip if selecting the already selected deal when no group is active
      if (dealName === currentDealName && !currentGroup) {
        return;
      }

      // Always clear the current group when directly selecting a deal
      if (currentGroup) {
        setCurrentGroup(null);
      }

      // Then select the deal
      if (dealName === "") {
        // Handle clearing the selection
        selectDeal(null);
      } else {
        // Handle selecting a deal, passing the previous period
        await selectDeal(dealName, previousPeriod);
      }
    }
  };

  // Handle portfolio selection
  const handlePortfolioSelection = async (portfolioName, groupInfo = null) => {
    if (isPortfolioLoading) {
      return;
    }

    const previousPeriod = selectedPeriod;

    if (groupInfo && groupInfo.isGroup) {
      // Handle portfolio group selection
      const groupToSelect = groupInfo.group;
      if (groupToSelect) {
        setCurrentPortfolioGroup(groupToSelect);
        await selectPortfolioGroup(groupInfo.groupId, previousPeriod);
      }
    } else {
      // Handle regular portfolio selection
      // Clear any current group when directly selecting a portfolio
      if (currentPortfolioGroup) {
        setCurrentPortfolioGroup(null);
      }

      if (portfolioName === "") {
        // Handle clearing the selection
        await selectPortfolio(null, previousPeriod);
      } else {
        // Convert to lowercase for selectPortfolio since it expects lowercase for data access
        await selectPortfolio(portfolioName.toLowerCase(), previousPeriod);
      }
    }
  };

  // Handle portfolio facility selection
  const handlePortfolioFacilitySelection = async (facilityName) => {
    if (isPortfolioLoading) {
      return;
    }

    const previousPeriod = selectedPeriod;
    await selectPortfolioFacility(facilityName, previousPeriod);
  };

  // Handle facility selection based on view mode
  const handleFacilitySelection = (facilityName) => {
    const previousPeriod = selectedPeriod;

    if (viewMode === 'portfolio') {
      handlePortfolioFacilitySelection(facilityName);
    } else {
      // Pass the previous period to maintain it if possible
      selectFacility(facilityName, previousPeriod);
    }
  };

  const handleArchiveToggle = () => {
    if (hasArchivedDealsData || viewingArchived) {
      toggleArchiveView();
    } else {
      window.alert("No archived deals are currently available.");
    }
  };

  const getActiveTabIndex = () => {
    const path = location.pathname;

    // If we're on a dynamic tab, return a special value
    if (dynamicTabs.some(tab => tab.path === path)) {
      return -1;
    }

    // Only the overview tab remains in the bottom navigation
    // Status and manage-groups are now in the top navbar
    return 0; // Default to Overview tab
  };

  const handleTabClick = (route) => {
    if (route === '/deals' || route === '/') {
      if (viewMode === 'portfolio') {
        if (currentPortfolioName) {
          // Use lowercase for URL to match data keys
          navigate(`/portfolio/${encodeURIComponent(currentPortfolioName.toLowerCase())}`);
        } else {
          navigate('/portfolio');
        }
      } else {
        if (currentDealName) {
          navigate(`/deals/${encodeURIComponent(currentDealName)}`);
        } else {
          navigate('/deals');
        }
      }
    } else {
      navigate(route);
    }
  };

  const getTabIcon = (tab) => {
    const iconProps = {
      className: `${isSmallScreen ? 'h-4 w-4' : 'h-5 w-5'}`
    };

    // Wrapper div to ensure consistent icon spacing and positioning
    const IconWrapper = ({ children }) => (
      <div className="flex items-center justify-center mr-2 flex-shrink-0" style={{ width: isSmallScreen ? '16px' : '20px', height: isSmallScreen ? '16px' : '20px' }}>
        {children}
      </div>
    );

    // Competition-related tabs
    if (tab.id.includes('competition') || tab.id.includes('market')) {
      if (tab.id.includes('analysis')) {
        return <IconWrapper><TbZoomMoney {...iconProps} /></IconWrapper>;
      } else if (tab.id.includes('market')) {
        return <IconWrapper><HiOutlineBuildingOffice2 {...iconProps} /></IconWrapper>;
      } else {
        return <IconWrapper><TbZoomMoney {...iconProps} /></IconWrapper>;
      }
    }

    // Financial model tab
    if (tab.id === 'financial-model') {
      return <IconWrapper><TbTableOptions {...iconProps} /></IconWrapper>;
    }

    // Finance-related tabs
    if (tab.id.includes('trend')) {
      return <IconWrapper><TbChartLine {...iconProps} /></IconWrapper>;
    } else {
      return <IconWrapper><TbReportMoney {...iconProps} /></IconWrapper>;
    }
  };

  const activeTabIndex = getActiveTabIndex();

  return (
    <nav className="fixed w-full top-1 duration-175 linear z-50 right-0 px-2 bg-white/30 transition-all">
      <div className="mt-[3px] w-full bg-white shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">

        {/* Two-row layout: Top bar + Controls */}
        <div className="w-full">
          {/* TOP ROW: Logo + View Mode Toggle + Avatar */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-navy-600">
            {/* Logo */}
            <div className="flex items-center px-2 sm:px-3">
              <Link to={
                viewMode === 'portfolio'
                  ? currentPortfolioName
                    ? `/portfolio/${encodeURIComponent(currentPortfolioName.toLowerCase())}`
                    : "/portfolio"
                  : currentDealName
                    ? `/deals/${encodeURIComponent(currentDealName)}`
                    : "/deals"
              }>
                {isSmallScreen ? (
                  <img src={miniLogo} width="44px" alt={`Return to ${viewMode} overview`} />
                ) : (
                  <img src={logo} alt={`Return to ${viewMode} overview`} width="160px" />
                )}
              </Link>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center">
              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={changeViewMode}
                isLoading={isTransitioning}
              />
            </div>

            {/* Action Buttons and Avatar */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Status and Groups icon buttons */}
              <TooltipHorizon
                content="Status"
                placement="bottom"
                extra="text-xs bg-opacity-50 text-gray-700"
                trigger={
                  <button
                    onClick={() => handleTabClick('/status')}
                    className={`p-2 transition-colors duration-200 hover:bg-blue-50 rounded-full focus:outline-none ${location.pathname.includes('/status')
                      ? 'text-[#002A4B] bg-blue-50'
                      : 'text-gray-600 hover:text-gray-800'
                      }`}
                    title="Status"
                  >
                    <MdAssessment className="h-7 w-7" />
                  </button>
                }
              />

              <TooltipHorizon
                content="Facility Groups"
                placement="bottom"
                extra="text-xs bg-opacity-50 text-gray-700"
                trigger={
                  <button
                    onClick={() => handleTabClick('/manage-groups')}
                    className={`p-2 transition-colors duration-200 hover:bg-blue-50 rounded-full focus:outline-none ${location.pathname.includes('/manage-groups')
                      ? 'text-[#002A4B] bg-blue-50'
                      : 'text-gray-600 hover:text-gray-800'
                      }`}
                    title="Facility Groups"
                  >
                    <MdLocationCity className="h-7 w-7" />
                  </button>
                }
              />

              {/* Avatar */}
              <div className="relative" ref={dropdownRef}>
                <TooltipHorizon
                  content={username ? `Hey, ${username}` : "Account"}
                  placement="bottom"
                  extra="text-xs bg-opacity-50 text-gray-700"
                  trigger={
                    <button
                      onClick={toggleDropdown}
                      className={`p-2 transition-colors duration-200 hover:bg-blue-50 rounded-full focus:outline-none ${isDropdownOpen
                        ? 'text-[#002A4B] bg-blue-50'
                        : 'text-gray-600 hover:text-gray-800'
                        }`}
                      title="Account"
                    >
                      <MdAccountCircle className="h-7 w-7" />
                    </button>
                  }
                />
                {isDropdownOpen && (
                  <div className="absolute right-0 top-10 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-navy-700 z-50">
                    <div className="py-2">
                      <p className="px-4 py-2 text-sm text-navy-700 dark:text-white">👋 Hey, {username}</p>
                      <hr className="my-2 border-gray-200 dark:border-white/20" />
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-navy-600"
                      >
                        Log Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* BOTTOM ROW: Controls */}
          <div className={`w-full px-4 py-3 flex justify-center gap-2 md:gap-4 md:items-center flex-col sm:flex-row`}>

            {viewMode === 'deals' ? (
              <>
                <div className="flex items-center">
                  <DealSelector
                    dealNames={dealNames}
                    size={isSmallScreen ? "small" : "medium"}
                    id="deal"
                    value={currentGroup ? currentGroup.id : currentDealName || ""}
                    onChange={handleDealSelection}
                    username={actualUsername}
                    currentGroup={currentGroup}
                    isDealLoading={isDealLoading}
                  />

                  {/* Archive button - exactly like original */}
                  {hasArchiveAccess && (
                    <>
                      {isLoading || isLoadingUser ? (
                        <div className="ml-2 p-2 text-gray-400">
                          <span className="animate-pulse">
                            <IoArchiveOutline />
                          </span>
                        </div>
                      ) : (hasArchivedDealsData || viewingArchived) ? (
                        <TooltipHorizon
                          content={viewingArchived ? "View current deals" : "View archived deals"}
                          placement="top"
                          extra="text-xs bg-opacity-50 text-gray-700 top-4"
                          trigger={
                            <button
                              onClick={handleArchiveToggle}
                              className={`ml-2 p-2 transition-colors duration-200 hover:bg-blue-50 rounded-full focus:outline-none hover:text-gray-600 text-blue-500`}
                              title={viewingArchived ? "Switch to current deals" : "Switch to archived deals"}
                            >
                              {viewingArchived ? (
                                <IoArchiveSharp />
                              ) : (
                                <IoArchiveOutline />
                              )}
                            </button>}
                        />
                      ) : null}

                      {/* Archive indicator badge */}
                      {viewingArchived && (
                        <div className="ml-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                          Archived
                        </div>
                      )}
                    </>
                  )}
                </div>

                <TimePeriodSelector
                  timePeriods={periodsList}
                  selectedPeriod={selectedPeriod}
                  setTimePeriods={setSelectedPeriod}
                  placeholder="Select a time period"
                  size={isSmallScreen ? "small" : "medium"}
                />

                <FacilitySelector
                  facilities={facilityNames}
                  selectedFacility={currentFacility?.details?.Facilities}
                  setSelectedFacility={handleFacilitySelection}
                  size={isSmallScreen ? "small" : "medium"}
                  isDisabled={!!currentGroup}
                />
              </>
            ) : (
              <>
                <div className="flex items-center">
                  <PortfolioNameSelector
                    portfolioNames={portfolioNames}
                    size={isSmallScreen ? "small" : "medium"}
                    value={currentPortfolioGroup ? currentPortfolioGroup.id : currentPortfolioName || ""}
                    onChange={handlePortfolioSelection}
                    isPortfolioLoading={isPortfolioLoading}
                    currentGroup={currentPortfolioGroup}
                    username={actualUsername}
                  />
                </div>

                {/* Time period selector for portfolio - show if any periods available */}
                {periodsList.length > 0 && (
                  <TimePeriodSelector
                    timePeriods={periodsList}
                    selectedPeriod={selectedPeriod}
                    setTimePeriods={setSelectedPeriod}
                    placeholder="Select a time period"
                    size={isSmallScreen ? "small" : "medium"}
                    isPortfolioLoading={isPortfolioLoading}
                    groupMonths
                  />
                )}

                {/* Portfolio facility selector - equivalent to FacilitySelector in deals */}
                {currentPortfolioName && (
                  <PortfolioSelector
                    portfolioFacilityNames={portfolioFacilityNames}
                    size={isSmallScreen ? "small" : "medium"}
                    value={currentPortfolioFacilityName || ""}
                    onChange={handlePortfolioFacilitySelection}
                    isPortfolioLoading={isPortfolioLoading}
                    isDisabled={!!currentPortfolioGroup}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* BOTTOM ROW - Tabs */}
      <div className="w-full bg-white border-t border-gray-200 dark:border-navy-600 px-4">
        <div className={`flex overflow-x-auto ${isSmallScreen ? 'justify-start' : ''}`}>
          {/* Main tabs */}
          <div
            className={`relative flex items-center px-6 py-3 text-sm cursor-pointer mr-2 rounded-t hover:bg-opacity-5 hover:bg-[#002A4B] transition-colors duration-200 font-medium ${activeTabIndex === 0
              ? "after:absolute after:content-[''] after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#f0b41c] after:rounded-t-md text-[#002A4B] font-semibold"
              : 'text-gray-600'
              }`}
            onClick={() => handleTabClick(viewMode === 'portfolio' ? '/portfolio' : '/deals')}
          >
            <MdDashboard className={`${isSmallScreen ? 'h-4 w-4' : 'h-5 w-5'} mr-2`} />
            <span>{isSmallScreen ? (viewMode === 'portfolio' ? 'Portfolio' : 'Deal') : (viewMode === 'portfolio' ? 'Portfolio Overview' : 'Deal Overview')}</span>
          </div>

          {/* Dynamic tabs for charts */}
          {dynamicTabs.map(tab => (
            <div
              key={tab.id}
              className={`relative flex items-center px-4 py-3 text-sm cursor-pointer mr-2 rounded-t hover:bg-opacity-5 hover:bg-[#002A4B] transition-colors duration-200 font-medium min-w-fit ${activeTab === tab.id
                ? "after:absolute after:content-[''] after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#f0b41c] after:rounded-t-md text-[#002A4B] font-semibold"
                : 'text-gray-600'
                }`}
              onClick={() => switchToTab(tab.id)}
            >
              {getTabIcon(tab)}
              <span className="whitespace-nowrap">{tab.title}</span>
              <button
                onClick={(e) => closeTab(tab.id, e)}
                className="ml-2 p-1 hover:bg-gray-200 rounded-full"
                title="Close tab"
              >
                <MdClose className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;